import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Paper,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import { omit } from 'lodash';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useNavigate } from 'react-router-dom';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { Order } from '../../interfaces/Order/Order';
import { OrderFilter } from '../../interfaces/Order/OrderFilter';
import { getOrders, updateOrderStatus } from '../../api/Order';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { OrdersTable } from './OrdersTable/OrdersTable';
import { OrderStatusDropdown } from '../../components/Dropdowns/OrderStatusDropdown';
import { Pagination } from '../../interfaces/Pagination';
import { OrderStatus } from '../../interfaces/Order/OrderStatus';
import { FilterSearchBox } from '../../components/SearchBox/FilterSearchBox';

const dateTimePickerStyle = {
  minWidth: 250,
  maxWidth: 250,
  flexGrow: 1,
  height: 'fit-content',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'none',
};

export const OrdersPage = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<PaginatedResponse<Order>>({
    data: [],
    totalCount: 0,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<OrderFilter>({
    pagination: { page: 0, pageSize: 10 },
  });
  const [loading, setLoading] = useState(false);

  const refreshOrders = (filters?: OrderFilter) => {
    setLoading(true);
    getOrders(filters ?? filter)
      .then((o) => {
        setOrders(o);
        setSelectedOrders([]);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshOrders();
  }, []);

  const handleSearch = () => {
    const newFilter: OrderFilter = {
      ...filter,
      pagination: { ...filter.pagination, page: 0 },
    };

    setFilter(newFilter);
    refreshOrders(newFilter);
    setSelectedOrders([]);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter: OrderFilter = { ...filter, pagination: p };

    setFilter(newFilter);
    refreshOrders(newFilter);
    setSelectedOrders([]);
  };

  const handleMarkAsShipped = () => {
    setLoading(true);
    updateOrderStatus(
      selectedOrders.map((order) => order.id),
      OrderStatus.CARGO_GIVEN,
    )
      .then(() => {
        toast.success('Sipariş durumları başarıyla güncellendi');
      })
      .catch((err) => toast.error(err.message))
      .finally(() => {
        handleSearch();
      });
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant='h5' sx={{ ...sharedStyles.pageTitle, m: 'auto' }}>
          Siparişler
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant='contained'
          color='primary'
          onClick={() => navigate('/sales/orders/scan')}
          startIcon={<QrCodeScannerIcon />}
          sx={{ ...sharedStyles.buttonText, m: 'auto' }}
        >
          Barkod Tara
        </Button>
      </Box>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <FilterSearchBox
            label='Müşteri Adı'
            filters={filter}
            onChange={setFilter}
            fieldName='customerName'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Müşteri Telefon'
            filters={filter}
            onChange={setFilter}
            fieldName='customerPhone'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Müşteri Email'
            filters={filter}
            onChange={setFilter}
            fieldName='email'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Kargo No'
            filters={filter}
            onChange={setFilter}
            fieldName='cargoNo'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Barkod'
            filters={filter}
            onChange={setFilter}
            fieldName='barcode'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Sipariş ID'
            filters={filter}
            onChange={setFilter}
            fieldName='id'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Ürün Adı'
            filters={filter}
            onChange={setFilter}
            fieldName='productName'
            onEnter={handleSearch}
          />

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Başlangıç'
                onChange={(newStartAt) =>
                  setFilter({ ...filter, startAt: newStartAt ?? undefined })
                }
                value={filter.startAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Bitiş'
                onChange={(newEndAt) =>
                  setFilter({ ...filter, endAt: newEndAt ?? undefined })
                }
                value={filter.endAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <OrderStatusDropdown
            label='Sipariş Durumu'
            value={filter.status}
            onChange={(newStatus) =>
              setFilter({ ...filter, status: newStatus })
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() => setFilter({ pagination: filter.pagination })}
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Sipariş Ara
            </Button>
          </Box>
        </Box>
      </Paper>

      {selectedOrders.length > 0 && (
        <Paper sx={{ mb: 1.5, p: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Typography>Seçilen siparişleri</Typography>

            <Button
              variant='contained'
              onClick={handleMarkAsShipped}
              startIcon={<LocalShippingIcon />}
              sx={{ ...sharedStyles.buttonText }}
            >
              Kargoya Verildi Olarak İşaretle
            </Button>

            <Button
              variant='contained'
              onClick={(ev) => setAnchorEl(ev.currentTarget)}
              startIcon={<PrintIcon />}
              sx={{ ...sharedStyles.buttonText }}
            >
              Yazdır
            </Button>

            <Popover
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Button
                  onClick={() => {
                    window.open(
                      `/print-orders/${selectedOrders
                        .map((order) => order.id.toString())
                        .join(',')}`,
                    );

                    setAnchorEl(null);
                  }}
                  sx={omit(
                    { ...sharedStyles.buttonText, px: 1.5, py: 0.75 },
                    'width',
                  )}
                >
                  Sipariş Detayı
                </Button>

                <Button
                  onClick={() => {
                    window.open(
                      `/print-shipment/${selectedOrders
                        .map((order) => order.id.toString())
                        .join(',')}`,
                    );

                    setAnchorEl(null);
                  }}
                  sx={omit(
                    { ...sharedStyles.buttonText, px: 1.5, py: 0.75 },
                    'width',
                  )}
                >
                  Kargo Çıktısı
                </Button>
              </Box>
            </Popover>
          </Box>
        </Paper>
      )}

      <OrdersTable
        orders={orders.data}
        totalCount={orders.totalCount}
        pagination={filter.pagination}
        onPagination={handlePagination}
        selectedOrders={selectedOrders}
        onSelectionChange={setSelectedOrders}
      />
    </Box>
  );
};
